import {
  DeleteRounded as DeleteIcon,
  ExpandMoreRounded as ExpandIcon,
  CalculateRounded as CalculatorIcon,
} from "@mui/icons-material";
import { Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionActions from "@mui/material/AccordionActions";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Component from "components/Component";
import Button from "components/common/Button";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import IconButton from "components/common/IconButton";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import TextField from "components/common/TextField";
import ProRataAddReturnCalculator from "components/common/calculators/ProRataAddReturnCalculator";
import ProRataCalculator from "components/common/calculators/ProRataCalculator";
import PersonSelect from "components/inputs/program/PersonSelect";
import * as config from "config";
import * as labels from "config.labels";
import { USE_PRO_RATA_ADD_RETURN_CALCULATOR } from "flags";
import { Fragment, useState } from "react";
import { connect } from "react-redux";
import * as staticDataService from "services/staticDataService";
import { useFeatureFlag } from "services/staticDataService";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

export const TYPE_ADDITIONAL = "Additional Premium";
export const TYPE_RETURNED = "Returned Premium";
export const TYPE_INSTALLMENT = "Installment";

const ActiveLayerPremiumAdjustmentsEditor = ({
  title = "Active Layer Premium Adjustments",
  subtitle,
  error,
  inception,
  expiration,
  layer,
  addPremiumAdjustment,
  editPremiumAdjustment,
  deletePremiumAdjustment,
  deletePremiumAdjustmentCustomOverrides,
  createPremiumAdjustment,
}) => {
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const { data: brokers } = staticDataService.useListBrokersQuery();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const PRO_RATA_BUTTON = translateLabel(
    labels.PREM_ADJ_EDITOR_PRO_RATA_BUTTON,
    {
      default: "Pro Rata",
    }
  );
  const CUSTOM_OVERRIDES_WARNING = translateLabel(
    labels.PREM_ADJ_EDITOR_CUSTOM_OVERRIDES_WARNING,
    {
      default:
        "Custom overrides have been applied to the premium\n" +
        "adjustment below. These will be reflected in the bordereau\n" +
        "report but not in Marmalade.",
    }
  );
  const DELETE_OVERRIDES_BUTTON = translateLabel(
    labels.PREM_ADJ_EDITOR_DELETE_OVERRIDES_BUTTON,
    {
      default: "Delete These Overrides",
    }
  );
  const ADDITIONAL_PREMIUM_TEXT = translateLabel(
    labels.PREM_ADJ_EDITOR_ADDITIONAL_PREMIUM_TEXT,
    { default: "Additional Premium" }
  );
  const RETURNED_PREMIUM_TEXT = translateLabel(
    labels.PREM_ADJ_EDITOR_RETURNED_PREMIUM_TEXT,
    { default: "Returned Premium" }
  );
  const PREMIUM_INSTALLMENT_TEXT = translateLabel(
    labels.PREM_ADJ_EDITOR_PREMIUM_INSTALLMENT_TEXT,
    { default: "Installment" }
  );
  const NOT_SET_TEXT = translateLabel(labels.PREM_ADJ_EDITOR_NOT_SET_TEXT, {
    default: "[Not Set]",
  });
  const START_DATE = translateLabel(labels.PREM_ADJ_EDITOR_START_DATE, {
    default: "Start Date",
  });
  const END_DATE = translateLabel(labels.PREM_ADJ_EDITOR_END_DATE, {
    default: "End Date",
  });
  const GROSS_PREMIUM_CHANGE = translateLabel(
    labels.PREM_ADJ_EDITOR_GROSS_PREMIUM_CHANGE,
    { default: "Gross Premium Change" }
  );
  const SHARE_OF_GROSS_PREMIUM_CHANGE = translateLabel(
    labels.PREM_ADJ_EDITOR_SHARE_OF_GROSS_PREMIUM_CHANGE,
    { default: "Share Of Gross Premium Change" }
  );
  const PAYMENT_TERMS_DAYS = translateLabel(
    labels.PREM_ADJ_EDITOR_PAYMENT_TERMS_DAYS,
    { default: "Payment Terms (Days)" }
  );
  const PAYMENT_TERMS_DATE = translateLabel(
    labels.PREM_ADJ_EDITOR_PAYMENT_TERMS_DATE,
    { default: "Payment Terms (Due Date)" }
  );
  const BROKER = translateLabel(labels.PREM_ADJ_EDITOR_BROKER, {
    default: "Broker",
  });
  const COMMENTS = translateLabel(labels.PREM_ADJ_EDITOR_COMMENTS, {
    default: "Comments",
  });
  const ADD_ADJUSTMENT = translateLabel(labels.PREM_ADJ_ADD_ADJUSTMENT, {
    default: "Add Adjustment",
  });

  const useProRataAddReturnCalc = useFeatureFlag(
    USE_PRO_RATA_ADD_RETURN_CALCULATOR,
    { default: true }
  );

  // Add early return if no layer
  if (!layer) {
    return null;
  }

  return (
    <>
      {useProRataAddReturnCalc ? (
        <ProRataAddReturnCalculator
          inception={inception}
          expiry={expiration}
          premium={layer.shareOfPremium}
          isOpen={calculatorOpen}
          onClose={() => setCalculatorOpen(false)}
          addPremiumAdjustment={(type, newEnddate, changeInPremium) => {
            const payload = {
              layerId: layer.id,
              type,
              endDate: newEnddate,
              changeInPremium,
            };
            if (layer.shareOfLine)
              payload.changeInGrossPremium =
                (changeInPremium / layer.shareOfLine) * 100;
            createPremiumAdjustment(payload);
          }}
        />
      ) : (
        <ProRataCalculator
          inception={inception}
          expiry={expiration}
          premium={layer.shareOfPremium}
          isOpen={calculatorOpen}
          onClose={() => setCalculatorOpen(false)}
        />
      )}

      <Component
        title={title}
        subtitle={subtitle}
        options={
          <Button
            onClick={() => setCalculatorOpen(!calculatorOpen)}
            startIcon={<CalculatorIcon />}
            size={"small"}
          >
            {PRO_RATA_BUTTON}
          </Button>
        }
        error={error}
      >
        {(layer?.premiumAdjustments ?? []).map((premiumAdjustment, i) => {
          const setValue = (key, value) =>
            editPremiumAdjustment({
              layerId: layer.id,
              premiumAdjustmentId: premiumAdjustment.id,
              edits: {
                [key]: value,
              },
            });

          const isExtension =
            premiumAdjustment.endDate != null &&
            expiration != null &&
            premiumAdjustment.endDate > expiration;

          return (
            <Accordion key={i}>
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Typography
                  variant={"subtitle1"}
                  sx={{ width: "50%", flexShrink: 0 }}
                >
                  {premiumAdjustment.type}
                </Typography>
                <Typography variant={"subtitle2"} sx={{ color: "$color-grey" }}>
                  {premiumAdjustment.startDate}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={config.GRID_SPACING}>
                  {premiumAdjustment.customOverrides && (
                    <Grid item xs={12}>
                      <Alert severity={"warning"}>
                        {CUSTOM_OVERRIDES_WARNING}
                        <br />
                        <br />
                        {Object.entries(premiumAdjustment.customOverrides).map(
                          ([k, v]) => (
                            <Fragment key={k}>
                              {k}
                              {":"}
                              <br />
                              {Object.entries(v).map(([k, v]) => (
                                <Fragment key={k}>
                                  &nbsp;&nbsp;&nbsp;&nbsp;{k}
                                  {": "}
                                  {JSON.stringify(v)}
                                  <br />
                                </Fragment>
                              ))}
                            </Fragment>
                          )
                        )}
                        <br />
                        <br />
                        <Button
                          variant={"contained"}
                          color={"error"}
                          onClick={() =>
                            deletePremiumAdjustmentCustomOverrides({
                              layerId: layer.id,
                              premiumAdjustmentId: premiumAdjustment.id,
                            })
                          }
                        >
                          {DELETE_OVERRIDES_BUTTON}
                        </Button>
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControl variant={"filled"} fullWidth>
                      <InputLabel>{"Type"}</InputLabel>
                      <Select
                        name={"type"}
                        value={premiumAdjustment.type ?? ""}
                        onChange={(e) => setValue("type", e.target.value)}
                        error={false}
                      >
                        <MenuItem value={TYPE_ADDITIONAL}>
                          {ADDITIONAL_PREMIUM_TEXT}
                        </MenuItem>
                        <MenuItem value={TYPE_RETURNED}>
                          {RETURNED_PREMIUM_TEXT}
                        </MenuItem>
                        <MenuItem value={TYPE_INSTALLMENT}>
                          {PREMIUM_INSTALLMENT_TEXT}
                        </MenuItem>
                        <MenuItem value={""}>{NOT_SET_TEXT}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormattedDatePicker
                      label={START_DATE}
                      value={premiumAdjustment.startDate}
                      onChangeDate={(date) => setValue("startDate", date)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormattedDatePicker
                      label={END_DATE}
                      value={premiumAdjustment.endDate}
                      onChangeDate={(date) => setValue("endDate", date)}
                      slotProps={
                        isExtension
                          ? {
                              textField: {
                                color: "warning",
                                focused: true,
                              },
                            }
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <PrettyNumberTextField
                      variant={"filled"}
                      label={GROSS_PREMIUM_CHANGE}
                      InputLabelProps={{ shrink: true }}
                      value={premiumAdjustment.grossPremiumChange}
                      onChangeNumber={(v) => setValue("grossPremiumChange", v)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <PrettyNumberTextField
                      variant={"filled"}
                      label={SHARE_OF_GROSS_PREMIUM_CHANGE}
                      InputLabelProps={{ shrink: true }}
                      value={premiumAdjustment.shareOfGrossPremiumChange}
                      onChangeNumber={(v) =>
                        setValue("shareOfGrossPremiumChange", v)
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <PrettyNumberTextField
                      variant={"filled"}
                      label={PAYMENT_TERMS_DAYS}
                      InputLabelProps={{ shrink: true }}
                      value={premiumAdjustment.paymentTerms?.days}
                      onChangeNumber={(v) =>
                        setValue("paymentTerms", v != null ? { days: v } : null)
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormattedDatePicker
                      disabled
                      label={PAYMENT_TERMS_DATE}
                      value={premiumAdjustment.paymentTerms?.dueDate}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PersonSelect
                      label={BROKER}
                      person={premiumAdjustment.broker}
                      personList={brokers}
                      onUpdatePerson={(broker) => setValue("broker", broker)}
                      canCreateNewBroker={false}
                      showEdit={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={COMMENTS}
                      value={premiumAdjustment.comments}
                      onChange={(e) => setValue("comments", e.target.value)}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
              <AccordionActions>
                <IconButton
                  icon={DeleteIcon}
                  onClick={() =>
                    deletePremiumAdjustment({
                      layerId: layer.id,
                      premiumAdjustmentId: premiumAdjustment.id,
                    })
                  }
                />
              </AccordionActions>
            </Accordion>
          );
        })}
        <Grid container sx={{ marginTop: 2 }}>
          <Button onClick={() => addPremiumAdjustment({ layerId: layer.id })}>
            {ADD_ADJUSTMENT}
          </Button>
        </Grid>
      </Component>
    </>
  );
};

export const mapDispatchToProps = {
  createPremiumAdjustment: pricingActions.createPremiumAdjustment,
  addPremiumAdjustment: pricingActions.addPremiumAdjustment,
  editPremiumAdjustment: pricingActions.editPremiumAdjustment,
  deletePremiumAdjustment: pricingActions.deletePremiumAdjustment,
  deletePremiumAdjustmentCustomOverrides:
    pricingActions.deletePremiumAdjustmentCustomOverrides,
};

export const mapStateToProps = (state) => {
  return {
    inception: programSelectors.getInception(state),
    expiration: programSelectors.selectResolvedProgram(state)?.expiration,
    layer: pricingSelectors.selectResolvedActiveLayer(state),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ActiveLayerPremiumAdjustmentsEditor);
